/**
 * `puncture` — spear, knife thrust, arrow, tusk.
 *
 * Deadlier per level than a cut and worse to treat: a narrow track you cannot
 * see the bottom of, bleeding inward as much as out, and carrying whatever was
 * on the point down with it. This is also the structure `bite` borrows (see
 * `bite.ts`), which is why the entries here are written to describe a hole
 * rather than a blade.
 */
import { fxAt4and5, ladder } from './authoring.js';
const PUNCTURE = [
    { bleed: 0, shock: 0, pain: 0, impair: 0, knock: 0, infect: 0 },
    { bleed: 1, shock: 1, pain: 1, impair: 0.05, knock: 0, infect: 0.04 },
    { bleed: 3, shock: 1.5, pain: 2, impair: 0.15, knock: 0, infect: 0.1 },
    { bleed: 7, shock: 3, pain: 4.5, impair: 0.4, knock: -1, infect: 0.18 },
    { bleed: 12, shock: 5, pain: 7, impair: 0.7, knock: -2, infect: 0.25 },
    { bleed: 18, shock: 8, pain: 10, impair: 1, knock: -4, infect: 0.3 },
];
export const PUNCTURE_TABLE = {
    head: ladder(PUNCTURE, {
        bleed: 0.8,
        shock: 1.6,
        knock: -2,
        fx: [[], [], [], ['knockdown'], ['break', 'knockdown'], ['kill']],
    }, [
        [
            '{weapon} scrapes {victim}’s scalp and leaves a bead of blood.',
            '{weapon} grazes {victim}’s temple.',
        ],
        [
            '{weapon} punches a hole in {victim}’s scalp.',
            '{weapon} pricks {victim}’s cheek and blood runs into {their} beard.',
        ],
        [
            '{weapon} drives into the flesh of {victim}’s face and scrapes bone.',
            '{weapon} tears through {victim}’s cheek and into the mouth beyond.',
        ],
        [
            '{weapon} punches into {victim}’s skull and stops on the bone.',
            '{weapon} drives through {victim}’s jaw and the teeth loosen in it.',
        ],
        [
            '{weapon} splinters {victim}’s skull and drives the bone inward.',
            '{weapon} punches through {victim}’s cheekbone and lodges.',
        ],
        [
            '{weapon} drives through {victim}’s skull and into the brain.',
            '{weapon} punches in at {victim}’s eye socket and does not stop.',
        ],
    ]),
    eye: ladder(PUNCTURE, {
        bleed: 0.4,
        shock: 1.4,
        fx: [[], [], ['blind'], ['blind'], ['blind'], ['blind', 'kill']],
    }, [
        [
            '{weapon} passes close by {victim}’s {part}.',
            '{weapon} pricks the lid of {victim}’s {part}.',
        ],
        [
            '{weapon} tears the lid of {victim}’s {part}.',
            '{weapon} punctures the flesh at the corner of {victim}’s {part}.',
        ],
        [
            '{weapon} puts out {victim}’s {part}.',
            '{weapon} drives into {victim}’s {part} and the sight goes with it.',
        ],
        [
            '{weapon} bursts {victim}’s {part} in its socket.',
            '{weapon} drives through {victim}’s {part} and grates on the bone behind.',
        ],
        [
            '{weapon} punches through {victim}’s {part} and into the socket.',
            '{weapon} empties {victim}’s {part} and cracks the bone around it.',
        ],
        [
            '{weapon} drives through {victim}’s {part} and into the brain.',
            '{weapon} enters at {victim}’s {part} and comes to rest deep in the skull.',
        ],
    ]),
    neck: ladder(PUNCTURE, { bleed: 2.5, knock: -1, fx: [[], [], [], [], [], ['kill']] }, [
        ['{weapon} grazes the skin of {victim}’s neck.', '{weapon} pricks {victim}’s throat.'],
        [
            '{weapon} punches a hole in the side of {victim}’s neck.',
            '{weapon} tears the skin of {victim}’s throat.',
        ],
        [
            '{weapon} drives into the muscle of {victim}’s neck.',
            '{weapon} punches into {victim}’s throat and the blood comes fast.',
        ],
        [
            '{weapon} drives deep into {victim}’s neck and blood follows it out.',
            '{weapon} punctures {victim}’s windpipe and the air whistles through it.',
        ],
        [
            '{weapon} opens the great vessels of {victim}’s neck from within.',
            '{weapon} drives through {victim}’s throat and grates on the spine.',
        ],
        [
            '{weapon} drives clean through {victim}’s neck.',
            '{weapon} punches through {victim}’s throat and severs the spine.',
        ],
    ]),
    chest: ladder(PUNCTURE, { bleed: 1.4, infect: 1.5, fx: [[], [], [], [], ['lodge'], ['kill']] }, [
        ['{weapon} scrapes across {victim}’s ribs.', '{weapon} pricks the skin of {victim}’s chest.'],
        [
            '{weapon} punches a shallow hole in {victim}’s chest.',
            '{weapon} tears the flesh over {victim}’s ribs.',
        ],
        [
            '{weapon} drives into the muscle of {victim}’s chest and turns on a rib.',
            '{weapon} punches between {victim}’s ribs and stops.',
        ],
        [
            '{weapon} drives deep into {victim}’s chest and {their} breath comes short.',
            '{weapon} punches through {victim}’s ribs and into the lung.',
        ],
        [
            '{weapon} drives into {victim}’s lung and the breath bubbles at the wound.',
            '{weapon} snaps against {victim}’s rib and the point stays behind.',
        ],
        [
            '{weapon} drives through {victim}’s chest and into the heart.',
            '{weapon} punches clean through {victim}’s chest and out the back.',
        ],
    ]),
    abdomen: ladder(PUNCTURE, { bleed: 1.2, infect: 3, fx: [[], [], [], ['lodge'], ['lodge'], ['kill']] }, [
        [
            '{weapon} scrapes across {victim}’s belly.',
            '{weapon} pricks the skin of {victim}’s flank.',
        ],
        [
            '{weapon} punches a shallow hole in {victim}’s belly.',
            '{weapon} tears the skin over {victim}’s hip.',
        ],
        [
            '{weapon} drives into the muscle of {victim}’s belly.',
            '{weapon} punches into {victim}’s flank and {victim} doubles over it.',
        ],
        [
            '{weapon} drives through the wall of {victim}’s belly and into the gut.',
            '{weapon} punches deep into {victim}’s side and comes out fouled.',
        ],
        [
            '{weapon} tears through {victim}’s gut and the belly begins to fill.',
            '{weapon} drives into {victim}’s liver and stays there.',
        ],
        [
            '{weapon} drives clean through {victim}’s belly.',
            '{weapon} punches through {victim}’s gut and grates on the spine.',
        ],
    ]),
    arm: ladder(PUNCTURE, { fx: fxAt4and5(['break'], ['sever']) }, [
        ['{weapon} scrapes along {victim}’s {part}.', '{weapon} pricks the skin of {victim}’s {part}.'],
        [
            '{weapon} punches a hole in {victim}’s {part}.',
            '{weapon} tears the flesh of {victim}’s {part}.',
        ],
        [
            '{weapon} drives into the muscle of {victim}’s {part}.',
            '{weapon} punches through {victim}’s {part} and out the other side.',
        ],
        [
            '{weapon} drives into {victim}’s {part} and turns on the bone.',
            '{weapon} tears the muscle from the bone of {victim}’s {part}.',
        ],
        [
            '{weapon} splinters the bone of {victim}’s {part}.',
            '{weapon} drives through {victim}’s {part} and the arm hangs useless.',
        ],
        [
            '{weapon} tears {victim}’s {part} away at the shoulder.',
            '{weapon} destroys {victim}’s {part} past any saving of it.',
        ],
    ]),
    hand: ladder(PUNCTURE, { bleed: 0.6, impair: 1.1, fx: fxAt4and5(['break'], ['sever']) }, [
        [
            '{weapon} scrapes across {victim}’s {part}.',
            '{weapon} pricks the back of {victim}’s {part}.',
        ],
        [
            '{weapon} punches a hole through {victim}’s {part}.',
            '{weapon} tears the web of {victim}’s {part}.',
        ],
        [
            '{weapon} drives through {victim}’s {part} and the fingers will not close.',
            '{weapon} punches through {victim}’s palm.',
        ],
        [
            '{weapon} drives through {victim}’s {part} and tears the tendons out of it.',
            '{weapon} pins {victim}’s {part} through and through.',
        ],
        [
            '{weapon} splinters the bones of {victim}’s {part}.',
            '{weapon} tears {victim}’s {part} into a ruin of bone and skin.',
        ],
        [
            '{weapon} tears {victim}’s {part} away at the wrist.',
            '{weapon} destroys {victim}’s {part} past any saving of it.',
        ],
    ]),
    leg: ladder(PUNCTURE, { knock: -2, fx: fxAt4and5(['break', 'knockdown'], ['sever', 'knockdown']) }, [
        [
            '{weapon} scrapes along {victim}’s {part}.',
            '{weapon} pricks the skin of {victim}’s {part}.',
        ],
        [
            '{weapon} punches a hole in {victim}’s {part}.',
            '{weapon} tears the flesh of {victim}’s {part}.',
        ],
        [
            '{weapon} drives into the muscle of {victim}’s {part} and {victim} staggers.',
            '{weapon} punches through {victim}’s thigh.',
        ],
        [
            '{weapon} drives deep into {victim}’s {part} and the leg gives.',
            '{weapon} tears through the back of {victim}’s {part}.',
        ],
        [
            '{weapon} splinters the bone of {victim}’s {part}.',
            '{weapon} drives through {victim}’s knee and the joint comes apart.',
        ],
        [
            '{weapon} tears {victim}’s {part} away at the thigh.',
            '{weapon} destroys {victim}’s {part} past any saving of it.',
        ],
    ]),
    foot: ladder(PUNCTURE, { bleed: 0.6, knock: -1, fx: fxAt4and5(['break'], ['sever']) }, [
        [
            '{weapon} scrapes across {victim}’s {part}.',
            '{weapon} pricks the skin of {victim}’s {part}.',
        ],
        [
            '{weapon} punches a hole through {victim}’s {part}.',
            '{weapon} tears the skin above {victim}’s ankle.',
        ],
        [
            '{weapon} drives through {victim}’s {part} and {victim} favours it.',
            '{weapon} pins {victim}’s {part} to the ground.',
        ],
        [
            '{weapon} drives through {victim}’s {part} and tears the tendon out of it.',
            '{weapon} punches through {victim}’s ankle and the joint gives.',
        ],
        [
            '{weapon} splinters the bones of {victim}’s {part}.',
            '{weapon} tears {victim}’s {part} into a ruin of bone and skin.',
        ],
        [
            '{weapon} tears {victim}’s {part} away at the ankle.',
            '{weapon} destroys {victim}’s {part} past any saving of it.',
        ],
    ]),
};
//# sourceMappingURL=puncture.js.map